import type { RefObject } from "react";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import type { SetState } from "../../vendor/src/features/remotion-editor/editor/context-provider";
import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { deleteItems } from "../../vendor/src/features/remotion-editor/editor/state/actions/delete-items";
import { generateRandomId } from "../../vendor/src/features/remotion-editor/editor/utils/generate-random-id";
import { editableTree } from "./group-split";
import {
  bindMixedCutGroupProof,
  captureClipboardGraph,
  mixedLinkedNestedSelection,
  pasteClipboardGraph,
  type ClipboardGraph,
} from "./clipboard-graph";
import {
  clipboardHtml,
  resolveClipboardPayload,
  type ClipboardPayload,
} from "./clipboard-codec";

function transactionToken(kind:"cut"|"paste",state:EditorState):string {
  let token=`clipboard-${kind}:${generateRandomId(12)}`;
  while(token===state.documentMutationOwner)token+='-next';
  return token;
}

export async function writeClipboardGraph(
  graph: ClipboardGraph,
  event?: ClipboardEvent,
): Promise<void> {
  const html = clipboardHtml(graph);
  if (html.length > 16_000_000) throw new Error("clipboard-too-large");
  if (event?.clipboardData) {
    event.clipboardData.setData("text/html", html);
    event.clipboardData.setData("text/plain", html);
    event.preventDefault();
    return;
  }
  if (typeof navigator === "undefined" || !navigator.clipboard)
    throw new Error("clipboard-unavailable");
  if (navigator.clipboard.write && typeof ClipboardItem !== "undefined")
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([html], { type: "text/plain" }),
      }),
    ]);
  else if (navigator.clipboard.writeText)
    await navigator.clipboard.writeText(html);
  else throw new Error("clipboard-unavailable");
}

export async function copyEditorSelection({
  stateRef,
  setState,
  ids,
  cut = false,
  event,
  write = writeClipboardGraph,
}: {
  stateRef: RefObject<EditorState>;
  setState: SetState;
  ids: readonly string[];
  cut?: boolean;
  event?: ClipboardEvent;
  write?: typeof writeClipboardGraph;
}): Promise<boolean> {
  const base = stateRef.current;
  const selected = cut ? ids.filter((id) => editableTree(base, id)) : [...ids];
  let graph = captureClipboardGraph(base, selected);
  if (!graph) {
    const mixedNestedMain = mixedLinkedNestedSelection(base, selected).length > 0;
    toast.info(
      mixedNestedMain
        ? "메인 컷과 그룹 내부 항목을 함께 복사하지 못했습니다. 원본과 그룹 구성을 확인해 주세요. 원본은 유지됩니다."
        : "복사할 클립의 원본 또는 그룹 구성을 확인해 주세요. 원본은 변경하지 않았습니다.",
    );
    return false;
  }
  const nested = cut ? mixedLinkedNestedSelection(base, selected) : [];
  let after: EditorState | null = null;
  if (cut && nested.length) {
    const withoutNested = deleteItems(base, nested, {
      isLinkingEnabled: false,
      timingApplied: true,
      internalDetach:true,
    });
    after = withoutNested === base
      ? null
      : deleteItems(withoutNested, selected.filter((id) => !nested.includes(id)), {
          isLinkingEnabled: true,
        });
    if (!after || after === withoutNested) {
      toast.info("선택한 클립을 안전하게 잘라내지 못했습니다. 원본은 유지됩니다.");
      return false;
    }
    const proven = bindMixedCutGroupProof(base, after, graph);
    if (!proven) {
      toast.info("그룹 조합을 안전하게 잘라내지 못했습니다. 원본은 유지됩니다.");
      return false;
    }
    graph = proven;
  } else if (cut) {
    after = deleteItems(base, [...selected], {
      isLinkingEnabled: base.isLinkingEnabled,
    });
  }
  if (cut && after?.undoableState === base.undoableState) {
    toast.info("선택한 클립을 잘라내지 못했습니다. 원본은 유지됩니다.");
    return false;
  }
  try {
    await write(graph, event);
  } catch (error) {
    toast.error(
      error instanceof Error && error.message === "clipboard-too-large"
        ? "복사할 자료가 너무 큽니다. 선택을 나눠 복사해 주세요. 원본은 유지됩니다."
        : "클립을 복사하지 못했습니다. 브라우저의 클립보드 접근을 허용하고 다시 시도해 주세요. 원본은 유지됩니다.",
    );
    return false;
  }
  if (after) {
    if (stateRef.current.undoableState !== base.undoableState) {
      toast.info(
        "복사하는 동안 다른 편집이 적용되어 잘라내지 않았습니다. 복사본은 붙여넣을 수 있습니다.",
      );
      return false;
    }
    const token = transactionToken("cut",base);
    let submitted: EditorState["undoableState"] | null = null;
    flushSync(() => setState({
      update: (current) => {
        if (current.undoableState !== base.undoableState) return current;
        const next = {
          ...current,
          undoableState: after.undoableState,
          selectedItems: after.selectedItems,
          timelineTimeEditApplied: true,
        };
        submitted = next.undoableState;
        return next;
      },
      commitToUndoStack: true,
      expectedUndoableState: base.undoableState,
      mutationOwner: token,
    }));
    if (stateRef.current.documentMutationOwner !== token &&
      (!submitted || stateRef.current.undoableState !== submitted)) {
      toast.info("복사하는 동안 편집 내용이 바뀌어 잘라내지 않았습니다. 복사본은 붙여넣을 수 있습니다.");
      return false;
    }
  }
  return true;
}

export function pasteEditorClipboard({
  stateRef,
  setState,
  payload,
  from,
  position = null,
}: {
  stateRef: RefObject<EditorState>;
  setState: SetState;
  payload: ClipboardPayload;
  from: number;
  position?: null | { x: number; y: number };
}): boolean {
  const graph = resolveClipboardPayload(stateRef.current, payload);
  if (!graph) {
    toast.error(
      "복사본의 원본 파일이나 그룹 자료가 부족합니다. 원래 작업에서 다시 복사해 주세요.",
    );
    return false;
  }
  if (graph.fps !== stateRef.current.undoableState.fps) {
    toast.info(
      `복사본은 ${graph.fps}fps, 현재 작업은 ${stateRef.current.undoableState.fps}fps입니다. 같은 프레임 속도의 작업에 붙여넣어 주세요.`,
    );
    return false;
  }
  const base = stateRef.current;
  const restoreMixedCut = Boolean(graph.mixedCutGroupProof && !position);
  const newTracks = !restoreMixedCut && base.activeSelectionSurface === "canvas";
  if (graph.mixedCutGroupProof &&
    pasteClipboardGraph(base, graph, from, position, newTracks) === base) {
    toast.info("그룹 상태나 붙여넣기 위치가 맞지 않아 합칠 수 없습니다. 잘라내기를 실행 취소해 다시 선택하거나, 다른 작업 또는 겹치지 않는 위치에 붙여넣어 주세요.");
    return false;
  }
  const token = transactionToken("paste",base);
  let submitted: EditorState["undoableState"] | null = null;
  flushSync(() => setState({
    update: (current) => {
      const next = pasteClipboardGraph(
        current,
        graph,
        from,
        position,
        !restoreMixedCut && current.activeSelectionSurface === "canvas",
      );
      if (next !== current) submitted = next.undoableState;
      return next;
    },
    commitToUndoStack: true,
    expectedUndoableState: graph.mixedCutGroupProof ? base.undoableState : undefined,
    mutationOwner: token,
  }));
  if (stateRef.current.documentMutationOwner !== token &&
    (!submitted || stateRef.current.undoableState !== submitted)) {
    toast.info("붙여넣기 전에 편집 내용이 바뀌었습니다. 원본을 유지했으니 다시 붙여넣어 주세요.");
    return false;
  }
  return true;
}
